import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import {
  assignGroupToInstructor,
  assignSupportCourseToInstructor,
  assignTrainingToInstructor,
  assignTutorialToInstructor,
  getAvailableGroups,
  getAvailableSupportCourses,
  getAvailableTrainings,
  getAvailableTutorials,
  getInstructorById,
  resetAvailable,
} from "store/slices/instructorsSlice";

const { Text } = Typography;

export const ASSIGN_TARGETS = {
  trainings: "trainings",
  supportCourses: "supportCourses",
  groups: "groups",
  tutorials: "tutorials",
};

// Cibles ou l'assignation remplace l'enseignant en place (un seul enseignant possible)
const REPLACING_TARGETS = [
  ASSIGN_TARGETS.supportCourses,
  ASSIGN_TARGETS.tutorials,
];

// Normalise n'importe quelle valeur en texte cherchable
const searchable = (...values) =>
  values
    .filter(Boolean)
    .map((value) => String(value))
    .join(" ")
    .toLowerCase();

const AssignToInstructorModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form?.useForm();

  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals,
  );
  const {
    instructor,
    availableTrainings,
    availableSupportCourses,
    availableGroups,
    availableTutorials,
    availableLoading,
    availableError,
    assigning,
  } = useSelector((state) => state?.instructors);

  const open = isModalOpen && modalType === "assign_to_instructor";
  const instructorId = modalProps?.instructorId;
  const target = modalProps?.target;

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  // Charger la liste correspondant a l'onglet ouvert
  useEffect(() => {
    if (!open || !instructorId || !target) return;

    setSearch("");
    setSelectedId(null);
    form?.resetFields();

    if (target === ASSIGN_TARGETS.trainings) {
      dispatch(getAvailableTrainings({ id: instructorId }));
    } else if (target === ASSIGN_TARGETS.supportCourses) {
      dispatch(getAvailableSupportCourses({ id: instructorId }));
    } else if (target === ASSIGN_TARGETS.groups) {
      dispatch(getAvailableGroups({ id: instructorId }));
    } else if (target === ASSIGN_TARGETS.tutorials) {
      dispatch(getAvailableTutorials({ id: instructorId }));
    }
  }, [dispatch, form, open, instructorId, target]);

  const items = useMemo(() => {
    if (target === ASSIGN_TARGETS.trainings) return availableTrainings || [];
    if (target === ASSIGN_TARGETS.supportCourses)
      return availableSupportCourses || [];
    if (target === ASSIGN_TARGETS.groups) return availableGroups || [];
    if (target === ASSIGN_TARGETS.tutorials) return availableTutorials || [];
    return [];
  }, [
    target,
    availableTrainings,
    availableSupportCourses,
    availableGroups,
    availableTutorials,
  ]);

  // Recherche cote front : le backend renvoie tout, sans pagination
  const filteredItems = useMemo(() => {
    const term = search?.trim()?.toLowerCase();
    if (!term) return items;

    return items?.filter((item) => {
      if (target === ASSIGN_TARGETS.trainings) {
        return searchable(
          item?.name,
          item?.categoryId?.titled,
          item?.subCategoryId?.titled,
          item?.schoolName,
        )?.includes(term);
      }
      if (target === ASSIGN_TARGETS.supportCourses) {
        return searchable(
          item?.subject?.title,
          item?.educationLevel,
          item?.wilaya,
          item?.status,
          item?.instructor?.fullName,
        )?.includes(term);
      }
      if (target === ASSIGN_TARGETS.tutorials) {
        return searchable(
          item?.title,
          item?.description,
          item?.categoryId?.titled,
          item?.subCategoryId?.titled,
          item?.level,
          item?.status,
          item?.instructor?.fullName,
        )?.includes(term);
      }
      return searchable(
        item?.groupName,
        item?.formationId?.name,
        item?.supportCourseId?.subject?.title,
        item?.groupeType,
        item?.groupState,
        item?.instructor?.fullName,
      )?.includes(term);
    });
  }, [items, search, target]);

  const selectedItem = useMemo(
    () => items?.find((item) => item?._id === selectedId) || null,
    [items, selectedId],
  );

  const trainingsColumns = [
    {
      title: t("instructors.assign.column.name"),
      dataIndex: "name",
      key: "name",
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: t("instructors.assign.column.category"),
      key: "category",
      render: (_, record) => (
        <Space size={4} wrap>
          {record?.categoryId?.titled && (
            <Tag color="purple">{record?.categoryId?.titled}</Tag>
          )}
          {record?.subCategoryId?.titled && (
            <Tag color="blue">{record?.subCategoryId?.titled}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("instructors.assign.column.type"),
      dataIndex: "type",
      key: "type",
      render: (types) => (
        <Space size={4} wrap>
          {(Array.isArray(types) ? types : [types])
            ?.filter(Boolean)
            ?.map((type) => (
              <Tag key={type}>{t(`trainings.type.${type}`)}</Tag>
            ))}
        </Space>
      ),
    },
    {
      title: t("instructors.assign.column.price"),
      key: "price",
      render: (_, record) =>
        `${record?.price?.particulier?.dzd || 0} ${t("trainings.currency")}`,
    },
  ];

  const supportCoursesColumns = [
    {
      title: t("instructors.assign.column.subject"),
      key: "subject",
      render: (_, record) => (
        <Text strong>{record?.subject?.title || t("common.untitled")}</Text>
      ),
    },
    {
      title: t("instructors.assign.column.level"),
      dataIndex: "educationLevel",
      key: "educationLevel",
      render: (level) => <Tag color="blue">{level}</Tag>,
    },
    {
      title: t("instructors.assign.column.wilaya"),
      dataIndex: "wilaya",
      key: "wilaya",
    },
    {
      title: t("instructors.assign.column.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "validated" ? "green" : "orange"}>
          {t(`supportCourse.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t("instructors.assign.column.currentInstructor"),
      key: "instructor",
      render: (_, record) =>
        record?.instructor?.fullName ? (
          <Text type="warning">{record?.instructor?.fullName}</Text>
        ) : (
          <Text type="secondary">{t("instructors.assign.noCurrentInstructor")}</Text>
        ),
    },
  ];

  const tutorialsColumns = [
    {
      title: t("instructors.assign.column.name"),
      dataIndex: "title",
      key: "title",
      render: (title) => <Text strong>{title}</Text>,
    },
    {
      title: t("instructors.assign.column.category"),
      key: "category",
      render: (_, record) => (
        <Space size={4} wrap>
          {record?.categoryId?.titled && (
            <Tag color="purple">{record?.categoryId?.titled}</Tag>
          )}
          {record?.subCategoryId?.titled && (
            <Tag color="blue">{record?.subCategoryId?.titled}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("instructors.assign.column.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "published" ? "green" : "orange"}>
          {t(`tutorials.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t("instructors.assign.column.students"),
      dataIndex: "studentsCount",
      key: "studentsCount",
      render: (count) => count || 0,
    },
    {
      title: t("instructors.assign.column.currentInstructor"),
      key: "instructor",
      render: (_, record) =>
        record?.hasInstructor && record?.instructor?.fullName ? (
          <Text type="warning">{record?.instructor?.fullName}</Text>
        ) : (
          <Text type="secondary">
            {t("instructors.assign.noCurrentInstructor")}
          </Text>
        ),
    },
  ];

  const groupsColumns = [
    {
      title: t("instructors.assign.column.group"),
      dataIndex: "groupName",
      key: "groupName",
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: t("instructors.assign.column.attachedTo"),
      key: "attachedTo",
      render: (_, record) =>
        record?.isForTraining ? (
          <Space size={4}>
            <Tag color="purple">{t("instructors.details.tabs.trainings")}</Tag>
            <Text>{record?.formationId?.name}</Text>
          </Space>
        ) : (
          <Space size={4}>
            <Tag color="cyan">
              {t("instructors.details.tabs.supportCourses")}
            </Tag>
            <Text>{record?.supportCourseId?.subject?.title}</Text>
          </Space>
        ),
    },
    {
      title: t("instructors.assign.column.type"),
      dataIndex: "groupeType",
      key: "groupeType",
      render: (type) => (
        <Tag color={type === "online" ? "blue" : "green"}>
          {t(`groups.type.${type}`)}
        </Tag>
      ),
    },
    {
      title: t("instructors.assign.column.students"),
      dataIndex: "studentsCount",
      key: "studentsCount",
      render: (count) => count || 0,
    },
    {
      title: t("instructors.assign.column.status"),
      dataIndex: "finished",
      key: "finished",
      render: (finished) => (
        <Badge
          status={finished ? "default" : "processing"}
          text={
            finished ? t("groups.status.finished") : t("groups.status.active")
          }
        />
      ),
    },
  ];

  const columnsByTarget = {
    [ASSIGN_TARGETS.trainings]: trainingsColumns,
    [ASSIGN_TARGETS.supportCourses]: supportCoursesColumns,
    [ASSIGN_TARGETS.tutorials]: tutorialsColumns,
    [ASSIGN_TARGETS.groups]: groupsColumns,
  };

  const columns = columnsByTarget[target] || groupsColumns;

  const handleClose = () => {
    form?.resetFields();
    setSearch("");
    setSelectedId(null);
    dispatch(resetAvailable());
    dispatch(closeModal());
  };

  const runAssignment = async (payment) => {
    let action;

    if (target === ASSIGN_TARGETS.trainings) {
      action = assignTrainingToInstructor({
        trainingId: selectedId,
        instructorId,
      });
    } else if (target === ASSIGN_TARGETS.supportCourses) {
      action = assignSupportCourseToInstructor({
        supportCourseId: selectedId,
        instructorId,
      });
    } else if (target === ASSIGN_TARGETS.tutorials) {
      action = assignTutorialToInstructor({
        tutorialId: selectedId,
        instructorId,
      });
    } else {
      action = assignGroupToInstructor({
        groupId: selectedId,
        instructorId,
        paymentType: payment?.paymentType,
        paymentValue: payment?.paymentValue,
      });
    }

    try {
      await dispatch(action)?.unwrap();

      notification?.success({
        message: t("instructors.assign.success"),
      });

      // Le profil complet (trainings / supportCourses / groups) est renvoye a jour
      dispatch(getInstructorById(instructorId));
      handleClose();
    } catch (error) {
      notification?.error({
        message: t("instructors.assign.error"),
        description:
          typeof error === "string"
            ? error
            : error?.message || t("common.error.generic"),
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedId || !instructorId) {
      notification?.warning({
        message: t("instructors.assign.selectRequired"),
      });
      return;
    }

    let payment = null;

    if (target === ASSIGN_TARGETS.groups) {
      try {
        payment = await form?.validateFields();
      } catch (error) {
        return;
      }
    }

    // Cours de soutien et tutoriel n'ont qu'un seul enseignant : l'assignation remplace l'actuel
    if (REPLACING_TARGETS.includes(target) && selectedItem?.instructor?._id) {
      Modal?.confirm({
        title: t(`instructors.assign.${target}.confirmTitle`),
        content: t(`instructors.assign.${target}.confirmContent`, {
          previous: selectedItem?.instructor?.fullName,
          next: instructor?.fullName,
        }),
        okText: t("instructors.assign.submit"),
        cancelText: t("common.cancel"),
        okButtonProps: { danger: true },
        onOk: () => runAssignment(payment),
      });
      return;
    }

    await runAssignment(payment);
  };

  const isGroupsPrerequisiteEmpty =
    target === ASSIGN_TARGETS.groups &&
    !availableLoading &&
    !availableError &&
    (items?.length || 0) === 0;

  return (
    <Modal
      title={t(`instructors.assign.title.${target}`)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={t("instructors.assign.submit")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !selectedId }}
      confirmLoading={assigning}
      destroyOnClose
      centered
      width={900}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {availableError && (
          <Alert type="error" showIcon message={String(availableError)} />
        )}

        {isGroupsPrerequisiteEmpty && (
          <Alert
            type="info"
            showIcon
            message={t("instructors.assign.groups.prerequisite")}
          />
        )}

        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("instructors.assign.search")}
          value={search}
          onChange={(event) => setSearch(event?.target?.value)}
        />

        <Table
          columns={columns}
          dataSource={filteredItems}
          rowKey="_id"
          size="small"
          loading={availableLoading}
          pagination={{ pageSize: 5, showSizeChanger: false }}
          scroll={{ x: true }}
          locale={{
            emptyText: t(`instructors.assign.empty.${target}`),
          }}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedId ? [selectedId] : [],
            onChange: (keys) => setSelectedId(keys?.[0] || null),
          }}
          onRow={(record) => ({
            onClick: () => setSelectedId(record?._id),
            style: { cursor: "pointer" },
          })}
        />

        {REPLACING_TARGETS.includes(target) &&
          selectedItem?.instructor?.fullName && (
            <Alert
              type="warning"
              showIcon
              message={t(`instructors.assign.${target}.replaceWarning`, {
                name: selectedItem?.instructor?.fullName,
              })}
            />
          )}

        {target === ASSIGN_TARGETS.groups && selectedId && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{ paymentType: "percentage", paymentValue: 40 }}
          >
            <Text strong>{t("instructors.assign.payment.title")}</Text>
            <Space align="start" style={{ marginTop: 8 }} wrap>
              <Form.Item
                name="paymentType"
                style={{ margin: 0, width: 220 }}
                rules={[
                  {
                    required: true,
                    message: t("groups.crud.form.error.required.paymentMode"),
                  },
                ]}
              >
                <Select
                  placeholder={t("groups.crud.form.placeholder.paymentMode")}
                  options={[
                    {
                      label: t(
                        "groups.crud.form.option.paymentMode.fixedPerHour",
                      ),
                      value: "fixedPerHour",
                    },
                    {
                      label: t("groups.crud.form.option.paymentMode.percentage"),
                      value: "percentage",
                    },
                  ]}
                />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev?.paymentType !== curr?.paymentType
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("paymentType") === "percentage" ? (
                    <Form.Item
                      name="paymentValue"
                      style={{ margin: 0, width: 220 }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "groups.crud.form.error.required.paymentValue.percentage",
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        max={100}
                        precision={2}
                        addonAfter="%"
                        placeholder={t(
                          "groups.crud.form.placeholder.paymentValue.percentage",
                        )}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="paymentValue"
                      style={{ margin: 0, width: 220 }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "groups.crud.form.error.required.paymentValue.fixedPerHour",
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        precision={2}
                        placeholder={t(
                          "groups.crud.form.placeholder.paymentValue.fixedPerHour",
                        )}
                      />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </Space>
          </Form>
        )}
      </Space>
    </Modal>
  );
};

export default AssignToInstructorModal;
